import { useState } from "react";
import { HelpCircle, X } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Inline help affordance — a small "?" pill that opens a tooltip popover with
 * an explanation. Operators new to the dashboard can self-discover what each
 * KPI means; veterans can ignore it.
 *
 * Usage: <HelpHint title="Pearson r">{...}</HelpHint>
 */
export function HelpHint({
  title,
  children,
  className,
  size = "xs",
}: {
  title?: string;
  children: React.ReactNode;
  className?: string;
  size?: "xs" | "sm";
}) {
  const [open, setOpen] = useState(false);
  return (
    <span className={cn("relative inline-flex", className)}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((x) => !x); }}
        className={cn(
          "grid place-items-center rounded-full border border-edge bg-surface text-text-muted hover:border-accent/40 hover:text-accent",
          size === "xs" ? "h-4 w-4" : "h-5 w-5",
        )}
        aria-label="Help"
      >
        <HelpCircle size={size === "xs" ? 10 : 12} />
      </button>
      {open && (
        <>
          <button
            className="fixed inset-0 z-30 cursor-default"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="absolute left-0 top-full z-40 mt-1 w-64 rounded-md border border-edge bg-surface p-3 text-[11px] leading-relaxed text-text shadow-xl">
            <div className="flex items-start justify-between gap-2">
              {title && <strong className="text-text">{title}</strong>}
              <button onClick={() => setOpen(false)} className="text-text-faint hover:text-text" aria-label="Close">
                <X size={11} />
              </button>
            </div>
            <div className="mt-1 text-text-muted">{children}</div>
          </div>
        </>
      )}
    </span>
  );
}
